import {useMemo} from 'react'
import type {AgentInfo} from '@github-ui/agent-sessions-types/agent-identity'
import {useResolvedAgentFilter} from './use-resolved-agent-filter'
import {useResolveAuthorIds} from './use-resolve-author-ids'

export interface TaskFilterParamsOptions {
  /** Whether filters are enabled (typically feature-flagged) */
  filtersEnabled: boolean
  /** Repository owner (required for author resolution) */
  owner: string
  /** Repository name (required for author resolution) */
  repo: string
  /** Selected author logins from the filter UI */
  selectedCreators: string[]
  /** Selected agent slugs from the filter UI */
  selectedAgents: string[]
  /** Selected detailed statuses from the filter UI (e.g. ['failed', 'merged']) */
  selectedStatuses?: string[]
  /** Sort direction from the filter UI ('newest' | 'oldest') */
  sortDirection?: string
  /** Free-text search query from the filter UI */
  searchQuery?: string
  /** Available agents list for slug→ID resolution */
  agents?: AgentInfo[]
  /** Whether agents are still loading */
  isLoadingAgents?: boolean
  /** Default agent IDs (e.g. from group-based sidebar filter) */
  defaultAgentIds?: number[]
  /** Current user's numeric ID for instant self-resolution */
  currentUserId?: number | null
  /** Current user's login for instant self-resolution */
  currentUserLogin?: string | null
  /** Whether "My sessions" mode is active */
  isFilteredByCurrentUser?: boolean
}

export interface TaskFilterParams {
  /** Resolved numeric creator IDs for the API, or undefined if no author filter */
  creatorIds: number[] | undefined
  /** Resolved custom agent names for the API */
  customAgentName: string | undefined
  /** Status filter values parsed from URL */
  statuses: string[] | undefined
  /** Sort param from URL */
  sort: string | undefined
  /** Free-text search query from URL */
  searchQuery: string | undefined
  /** Resolved numeric agent IDs for the API */
  agentIds: number[] | undefined
  /** Whether all filter resolutions are complete and data can be fetched */
  filtersReady: boolean
}

/**
 * Resolves parsed filter state into API-ready query values.
 *
 * Encapsulates:
 * - Resolving author logins → numeric creator IDs (via assignees endpoint)
 * - Resolving agent display slugs → IDs/custom names
 * - Passing through selectedStatuses, sortDirection, and searchQuery
 * - Gating all of the above behind `filtersEnabled`
 *
 * Both /copilot/agents and /{owner}/{repo}/agents use this to avoid
 * duplicating the filter→API translation layer.
 */
export function useTaskFilterParams({
  filtersEnabled,
  owner,
  repo,
  selectedCreators,
  selectedAgents,
  selectedStatuses,
  sortDirection,
  searchQuery,
  agents,
  isLoadingAgents,
  defaultAgentIds,
  currentUserId,
  currentUserLogin,
  isFilteredByCurrentUser = false,
}: TaskFilterParamsOptions): TaskFilterParams {
  // Resolve agent display slugs to numeric IDs and custom agent names
  const {resolvedAgentIds, resolvedCustomAgentNames, agentFilterReady} = useResolvedAgentFilter(
    filtersEnabled ? selectedAgents : [],
    agents,
    isLoadingAgents,
    defaultAgentIds,
  )

  // Determine which author logins need resolution via the assignees endpoint.
  // Current user can be resolved instantly without a network call.
  const loginsToResolve = useMemo(() => {
    if (!filtersEnabled || selectedCreators.length === 0) return undefined
    if (isFilteredByCurrentUser) return undefined
    return selectedCreators.filter(login => login !== currentUserLogin)
  }, [filtersEnabled, selectedCreators, isFilteredByCurrentUser, currentUserLogin])

  const {resolvedAuthorIds, isResolvingAuthors} = useResolveAuthorIds(owner, repo, loginsToResolve)

  // Build final creator IDs array
  const creatorIds = useMemo(() => {
    if (!filtersEnabled) return undefined
    if (isFilteredByCurrentUser) {
      return currentUserId ? [currentUserId] : undefined
    }
    if (selectedCreators.length === 0) return undefined

    const ids: number[] = []

    // Instantly resolve current user if they're in the selected list
    if (currentUserLogin && selectedCreators.includes(currentUserLogin) && currentUserId) {
      ids.push(currentUserId)
    }

    // Add IDs resolved via the assignees endpoint
    if (resolvedAuthorIds) {
      ids.push(...resolvedAuthorIds)
    }

    return ids.length > 0 ? ids : undefined
  }, [filtersEnabled, isFilteredByCurrentUser, selectedCreators, currentUserId, currentUserLogin, resolvedAuthorIds])

  // Author filter is ready when we don't need resolution or it's complete
  const authorFilterReady =
    !filtersEnabled || selectedCreators.length === 0 || isFilteredByCurrentUser || !isResolvingAuthors

  return {
    creatorIds,
    customAgentName: resolvedCustomAgentNames?.join(',') || undefined,
    statuses: filtersEnabled && selectedStatuses && selectedStatuses.length > 0 ? selectedStatuses : undefined,
    sort: filtersEnabled && sortDirection && sortDirection !== 'newest' ? sortDirection : undefined,
    searchQuery: filtersEnabled && searchQuery ? searchQuery : undefined,
    agentIds: resolvedAgentIds,
    filtersReady: agentFilterReady && authorFilterReady,
  }
}
